import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Analytics } from "@/lib/analytics";

const THRESHOLDS = [25, 50, 75, 100];

/**
 * Hook to track scroll depth on a page.
 * Sends Analytics.scrollDepth() events at 25%, 50%, 75%, and 100%.
 * Avoids duplicate events per session/page.
 */
export const useScrollDepth = () => {
  const location = useLocation();
  const trackedThresholds = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Reset tracked thresholds when path changes
    trackedThresholds.current.clear();

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      // Avoid division by zero on short pages
      if (scrollHeight <= 0) return;

      const scrollPosition = window.scrollY;
      const scrollPercentage = Math.round((scrollPosition / scrollHeight) * 100);

      THRESHOLDS.forEach((threshold) => {
        if (scrollPercentage >= threshold && !trackedThresholds.current.has(threshold)) {
          trackedThresholds.current.add(threshold);
          Analytics.scrollDepth(threshold, location.pathname);
        }
      });
    };

    // Add scroll listener with passive flag for performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Check initial scroll position (e.g., if user refreshes mid-page)
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname]);
};
